import React, { useEffect, useState } from 'react';
import { Form, Button, Offcanvas, Spinner } from 'react-bootstrap';
import Select, { SingleValue } from 'react-select';
import { toast } from 'react-toastify';
import { GetAgingStatusesAsync } from '../../../services/agingService/agingService';
import { getWaiverTypes, WaiverType } from '../../../services/Lookups/WaiverLookupService';
import { AgingStatusDto, SelectOption } from '../../../interfaces/agingReport/agingReport';
import { useAuth } from '../../../firebase/AuthProvider';
import { getAllRegions } from '../../../services/userServices/UserInfoService';
import { Region } from '../../../interfaces/identity/Region';

interface Props {
    show: boolean;
    onHide: () => void;
    selectedStatusOption: SelectOption | null;
    setSelectedStatusOption: (option: SelectOption | null) => void;
    selectedWaiverOptions: SelectOption[];
    setSelectedWaiverOptions: (options: SelectOption[]) => void;
    selectedRegionOptions: SelectOption[]; // <-- Add this
    setSelectedRegionOptions: (options: SelectOption[]) => void; // <-- Add this
    onApply: () => void;
    statuses: AgingStatusDto[];
}

const AgingReportFilter: React.FC<Props> = ({
    show,
    onHide,
    selectedStatusOption,
    setSelectedStatusOption,
    selectedWaiverOptions,
    setSelectedWaiverOptions,
    selectedRegionOptions, // <-- Add this
    setSelectedRegionOptions, // <-- Add this
    onApply,
    statuses
}) => {
    const { currentUser } = useAuth();
    const [waivers, setWaivers] = useState<WaiverType[]>([]);
    const [regions, setRegions] = useState<Region[]>([]); // <-- Add this
    const [statusLoading, setStatusLoading] = useState<boolean>(false);
    const [waiverLoading, setWaiverLoading] = useState<boolean>(false);
    const [regionLoading, setRegionLoading] = useState<boolean>(false); // <-- Add this

    useEffect(() => {
        getWaivers();
        getRegions(); // <-- Add this
        // eslint-disable-next-line
    }, []);


    const getWaivers = async () => {
        if (!currentUser) return;
        setWaiverLoading(true);
        const token = await currentUser.getIdToken();
        const res = await getWaiverTypes(token);
        if (res) {
            setWaivers(res);
        }
        setWaiverLoading(false);
    };

    const getRegions = async () => {
        if (!currentUser) return;
        setRegionLoading(true);
        const token = await currentUser.getIdToken();
        try {
            const res = await getAllRegions(token);
            if (res && res.data) {
                setRegions(res.data);
            }
        } catch (e) {
            toast.error("Failed to load regions");
        }
        setRegionLoading(false);
    };

    const statusOptions: SelectOption[] = [
        { value: 0, label: "All" },
        ...statuses.map(s => ({
            value: s.id,
            label: s.name
        }))
    ];

    const waiverOptions: SelectOption[] = [
        { value: 0, label: "All" },
        ...waivers.map(w => ({
            value: w.id,
            label: w.name
        }))
    ];

    const regionOptions: SelectOption[] = [
        { value: 0, label: "All" },
        ...regions.map(r => ({
            value: r.regionId,
            label: r.regionName
        }))
    ];

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" className="aging-report-filter-offcanvas" style={{ width: '400px' }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title className="aging-report-filter-title">Filter Aging Report</Offcanvas.Title>
            </Offcanvas.Header>
            <hr className="aging-report-filter-divider" />
            <Offcanvas.Body className='p-4 d-flex flex-column'>
                <Form className="flex-grow-1">
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Select<{ value: number; label: string }, false>
                            options={statusOptions}
                            value={selectedStatusOption}
                            onChange={(option: SingleValue<{ value: number; label: string }>) => setSelectedStatusOption(option)}
                            isLoading={statusLoading}
                            isClearable
                            className="react-select"
                            classNamePrefix="react-select"
                            placeholder={statusLoading ? "Loading..." : "Select a status"}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Waiver Type</Form.Label>
                        <Select<{ value: number; label: string }, true>
                            options={waiverOptions}
                            value={selectedWaiverOptions}
                            onChange={(options) => setSelectedWaiverOptions(options as SelectOption[])}
                            isMulti
                            isClearable
                            isLoading={waiverLoading}
                            className="react-select"
                            classNamePrefix="react-select"
                            placeholder={waiverLoading ? "Loading..." : "Select waiver type(s)"}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Region</Form.Label>
                        <Select<{ value: number; label: string }, true>
                            options={regionOptions}
                            value={selectedRegionOptions}
                            onChange={(options) => setSelectedRegionOptions(options as SelectOption[])}
                            isMulti
                            isClearable
                            isLoading={regionLoading}
                            className="react-select"
                            classNamePrefix="react-select"
                            placeholder={regionLoading ? "Loading..." : "Select region(s)"}
                        />
                    </Form.Group>
                </Form>
                <hr className="aging-report-filter-divider" />
                <div className="grid grid-cols-2 gap-3 mt-auto justify-center">
                    <Button
                        onClick={() => {
                            setSelectedStatusOption(null);
                            setSelectedWaiverOptions([]);
                            setSelectedRegionOptions([]);
                        }}
                        className="aging-report-filter-clear-btn"
                    >
                        Clear
                    </Button>
                    <Button
                        onClick={() => {
                            onHide();
                            onApply();
                        }}
                        className="filter-apply-btn"
                    >
                        Apply Filter
                    </Button>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AgingReportFilter;